/**
 * Runtime configuration, fetched from /config.json rather than baked in at build
 * time.
 *
 * The deciding value is the Cognito client id: it only exists once the auth stack
 * is deployed. Reading it at build time would force a deploy → read-outputs →
 * rebuild → re-upload cycle. The web stack writes this file with resolved values,
 * so one `cdk deploy` is enough and the same bundle works in any environment.
 */

export type Config = {
  cognitoDomain: string;
  cognitoClientId: string;
  apiBase: string;
  chatUrl: string;
  /** Cognito requires the redirect URI to match the registered callback exactly. */
  redirectUri: string;
};

let cached: Config | null = null;

function required(source: Record<string, unknown>, key: string): string {
  const value = source[key];
  if (typeof value !== 'string' || value.length === 0) {
    throw new Error(`config.json is missing "${key}"`);
  }
  return value;
}

export async function loadConfig(): Promise<Config> {
  if (cached) {
    return cached;
  }

  const response = await fetch('/config.json', { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`Kon de configuratie niet laden (HTTP ${response.status})`);
  }

  const raw = (await response.json()) as Record<string, unknown>;
  cached = {
    cognitoDomain: required(raw, 'cognitoDomain'),
    cognitoClientId: required(raw, 'cognitoClientId'),
    apiBase: required(raw, 'apiBase').replace(/\/$/, ''),
    chatUrl: required(raw, 'chatUrl'),
    redirectUri: `${window.location.origin}/`,
  };
  return cached;
}

/**
 * The loaded config. Throws if called before `loadConfig()` resolved — `main.ts`
 * awaits it before mounting, so anything rendered can rely on this.
 */
export function config(): Config {
  if (!cached) {
    throw new Error('Config is not loaded yet');
  }
  return cached;
}
